/**
 * App Providers
 *
 * Wraps the Account Portal with all necessary providers:
 * - Apollo Client for GraphQL
 * - Auth Provider for authentication
 * - UserPreferences for user settings
 * - Authorization Provider for permissions
 * - ThemeSyncProvider (reads themePreference from Apollo cache via UserPreferencesProvider)
 */

import { ReactNode } from 'react';
import { ApolloProvider } from '@bloomcode/apollo';
import { AuthProvider } from '@bloomcode/auth';
import {
  UserPreferencesProvider,
  AuthorizationProvider,
  SharedModulesProvider,
  ThemeSyncProvider,
} from '@bloomcode/shared-modules';
import { TooltipProvider } from '@bloomcode/ui';
import { getAppUrl } from '@bloomcode/config';

const GRAPHQL_ENDPOINT = '/api/graphql';
const AUTH_PORTAL_URL = getAppUrl('portal-auth');

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ApolloProvider
      config={{ graphqlEndpoint: GRAPHQL_ENDPOINT, enableLogging: import.meta.env.DEV }}
    >
      <AuthProvider
        apiBaseUrl={GRAPHQL_ENDPOINT.replace('/graphql', '')}
        authPortalUrl={AUTH_PORTAL_URL}
        redirectIfNoSession={true}
      >
        <UserPreferencesProvider>
          <AuthorizationProvider
            onSessionExpired={() => {
              window.location.href = AUTH_PORTAL_URL;
            }}
            onUnauthorized={() => {}}
          >
            <SharedModulesProvider
              config={{
                currentAppId: 'portal-account',
                showSearch: true,
                showFavorites: true,
                showRecent: true,
                maxRecentItems: 10,
              }}
            >
              <ThemeSyncProvider>
                <TooltipProvider delayDuration={300}>{children}</TooltipProvider>
              </ThemeSyncProvider>
            </SharedModulesProvider>
          </AuthorizationProvider>
        </UserPreferencesProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default Providers;
